
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaChevronRight,
  FaCreditCard,
  FaExclamationCircle,
  FaLock,
  FaPercentage,
  FaSearch,
  FaShieldAlt,
  FaStore,
  FaTimes,
  FaSyncAlt,
  FaSave,
  FaCheck,
} from "react-icons/fa";
import "./PaymentSettingsSuperadmin.css";

const API =
  "https://acesso.cronosagenda.com.br/api/systmanag/pagamentos/configuracoes";

const PERCENTUAIS = [10, 25, 30, 50, 100];

const normalizarItem = (item) => ({
  ...item,
  taxa_cronos: String(item.taxa_cronos ?? 0),
  pagamento_online_liberado: Boolean(item.pagamento_online_liberado),
  permitir_10: Boolean(item.permitir_10),
  permitir_25: Boolean(item.permitir_25),
  permitir_30: Boolean(item.permitir_30),
  permitir_50: Boolean(item.permitir_50),
  permitir_100: Boolean(item.permitir_100),
});

export default function PaymentSettingsSuperadmin() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const [modalAberto, setModalAberto] = useState(false);
  const [barbeariaSelecionada, setBarbeariaSelecionada] = useState(null);
  const [busca, setBusca] = useState("");

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    setError("");

    try {
      const response = await axios.get(API, {
        withCredentials: true,
      });

      const dados = Array.isArray(response.data) ? response.data : [];
      setItems(dados.map(normalizarItem));
    } catch (e) {
      setError(
        e.response?.data?.error ||
          "Não foi possível carregar as configurações de pagamento."
      );
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const fecharModal = useCallback(() => {
    if (savingId !== null) return;

    setModalAberto(false);
    setBarbeariaSelecionada(null);
    setBusca("");
    setMessage("");
    setError("");
  }, [savingId]);

  useEffect(() => {
    if (!modalAberto) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        fecharModal();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [modalAberto, fecharModal]);

  const abrirModal = async () => {
    setMessage("");
    setError("");
    setBusca("");
    setBarbeariaSelecionada(null);
    setModalAberto(true);

    if (items.length === 0) {
      await load();
    }
  };

  const selecionarBarbearia = (item) => {
    setBarbeariaSelecionada(item.barbearia_id);
    setMessage("");
    setError("");
  };

  const voltarParaLista = () => {
    if (savingId !== null) return;

    setBarbeariaSelecionada(null);
    setMessage("");
    setError("");
  };

  const update = (id, field, value) => {
    setItems((current) =>
      current.map((item) =>
        item.barbearia_id === id
          ? { ...item, [field]: value }
          : item
      )
    );
  };

  const save = async (item) => {
    const taxa = Number(item.taxa_cronos);

    if (
      item.taxa_cronos.trim() === "" ||
      !Number.isFinite(taxa) ||
      taxa < 0
    ) {
      setError(
        "Informe uma taxa Cronos válida, igual ou superior a zero."
      );
      setMessage("");
      return;
    }

    setSavingId(item.barbearia_id);
    setMessage("");
    setError("");

    try {
      await axios.put(
        `${API}/${item.barbearia_id}`,
        {
          pagamento_online_liberado: Boolean(
            item.pagamento_online_liberado
          ),
          permitir_10: Boolean(item.permitir_10),
          permitir_25: Boolean(item.permitir_25),
          permitir_30: Boolean(item.permitir_30),
          permitir_50: Boolean(item.permitir_50),
          permitir_100: Boolean(item.permitir_100),
          taxa_cronos: item.taxa_cronos,
        },
        { withCredentials: true }
      );

      setMessage(`Configurações de ${item.nome} salvas com sucesso.`);
      await load();
    } catch (e) {
      setError(
        e.response?.data?.error ||
          `Não foi possível salvar as configurações de ${item.nome}.`
      );
    } finally {
      setSavingId(null);
    }
  };

  const barbeariasFiltradas = items.filter((item) => {
    const termo = busca.trim().toLowerCase();

    if (!termo) return true;

    return [item.nome, item.subdominio, item.barbearia_id].some(
      (valor) => String(valor ?? "").toLowerCase().includes(termo)
    );
  });

  const itemAtual = items.find(
    (item) => item.barbearia_id === barbeariaSelecionada
  );

  const totalLiberadas = items.filter(
    (item) => item.pagamento_online_liberado
  ).length;

  return (
    <div className="ps-root">
      <section className="ps-open-card">
        <div className="ps-open-info">
          <div className="ps-open-icon">
            <FaCreditCard />
          </div>

          <div className="ps-open-copy">
            <span className="ps-eyebrow">CRONOS SISTEMAS / CONTROLE</span>
            <h2>Configurações de pagamento</h2>
            <p>
              Controle as permissões e taxas da plataforma em cada
              barbearia.
            </p>
          </div>
        </div>

        <button
          type="button"
          className="ps-button ps-button-primary"
          onClick={abrirModal}
        >
          <FaCreditCard />
          Gerenciar pagamentos
          <FaChevronRight className="ps-button-arrow" />
        </button>
      </section>

      {modalAberto && (
        <div
          className="ps-overlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) fecharModal();
          }}
        >
          <section
            className="ps-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ps-modal-title"
          >
            <header className="ps-modal-header">
              <div className="ps-modal-brand">
                <div className="ps-brand-mark">
                  <span>C</span>
                </div>

                <div>
                  <span className="ps-brand-name">CRONOS SISTEMAS</span>
                  <span className="ps-brand-caption">
                    PAINEL ADMINISTRATIVO
                  </span>
                </div>
              </div>

              <button
                type="button"
                className="ps-close-button"
                onClick={fecharModal}
                disabled={savingId !== null}
                aria-label="Fechar configurações"
                title="Fechar"
              >
                <FaTimes />
              </button>
            </header>

            <div className="ps-modal-heading">
              <div className="ps-heading-icon">
                {barbeariaSelecionada !== null ? (
                  <FaStore />
                ) : (
                  <FaCreditCard />
                )}
              </div>

              <div>
                <span className="ps-eyebrow">GESTÃO FINANCEIRA</span>
                <h2 id="ps-modal-title">
                  {barbeariaSelecionada !== null
                    ? "Permissões de pagamento"
                    : "Gerenciar pagamentos"}
                </h2>
                <p>
                  {barbeariaSelecionada !== null
                    ? "Configure as regras desta unidade."
                    : "Selecione uma barbearia para gerenciar suas permissões."}
                </p>
              </div>
            </div>

            <div className="ps-modal-body">
              {error && (
                <div className="ps-alert ps-alert-error" role="alert">
                  <FaExclamationCircle />
                  <span>{error}</span>
                </div>
              )}

              {message && (
                <div className="ps-alert ps-alert-success" role="status">
                  <FaCheckCircle />
                  <span>{message}</span>
                </div>
              )}

              {busy ? (
                <div className="ps-empty">
                  <FaSyncAlt className="ps-spinner" />
                  <strong>Carregando configurações</strong>
                  <span>Consultando os dados da plataforma...</span>
                </div>
              ) : barbeariaSelecionada === null ? (
                <>
                  <div className="ps-summary">
                    <div className="ps-summary-card">
                      <div className="ps-summary-icon">
                        <FaStore />
                      </div>
                      <div>
                        <span className="ps-summary-label">
                          BARBEARIAS CADASTRADAS
                        </span>
                        <strong>{items.length}</strong>
                      </div>
                    </div>

                    <div className="ps-summary-card">
                      <div className="ps-summary-icon ps-summary-icon-active">
                        <FaCheckCircle />
                      </div>
                      <div>
                        <span className="ps-summary-label">
                          RECURSO LIBERADO
                        </span>
                        <strong>{totalLiberadas}</strong>
                      </div>
                    </div>
                  </div>

                  <div className="ps-list-heading">
                    <div>
                      <h3>Unidades da plataforma</h3>
                      <p>Selecione para editar as configurações.</p>
                    </div>

                    <button
                      type="button"
                      className="ps-icon-button"
                      onClick={load}
                      title="Atualizar lista"
                      aria-label="Atualizar lista"
                    >
                      <FaSyncAlt />
                    </button>
                  </div>

                  <div className="ps-search">
                    <FaSearch />
                    <input
                      type="search"
                      className="ps-input"
                      placeholder="Buscar nome, subdomínio ou ID..."
                      value={busca}
                      onChange={(event) => setBusca(event.target.value)}
                      aria-label="Buscar barbearia"
                    />
                  </div>

                  {items.length === 0 ? (
                    <div className="ps-empty">
                      <FaStore />
                      <strong>Nenhuma barbearia encontrada</strong>
                      <span>
                        Verifique a conexão e tente carregar novamente.
                      </span>

                      <button
                        type="button"
                        className="ps-button ps-button-secondary"
                        onClick={load}
                      >
                        <FaSyncAlt /> Tentar novamente
                      </button>
                    </div>
                  ) : barbeariasFiltradas.length === 0 ? (
                    <div className="ps-empty">
                      <FaSearch />
                      <strong>Nenhum resultado</strong>
                      <span>
                        Tente outro nome, subdomínio ou identificador.
                      </span>
                    </div>
                  ) : (
                    <div className="ps-list">
                      {barbeariasFiltradas.map((item) => (
                        <button
                          type="button"
                          className="ps-list-item"
                          key={item.barbearia_id}
                          onClick={() => selecionarBarbearia(item)}
                        >
                          <span className="ps-list-left">
                            <span className="ps-store-icon">
                              <FaStore />
                            </span>

                            <span className="ps-list-copy">
                              <strong>
                                {item.nome || "Barbearia sem nome"}
                              </strong>
                              <small>
                                {item.subdominio ||
                                  `ID ${item.barbearia_id}`}
                              </small>
                            </span>
                          </span>

                          <span className="ps-list-right">
                            <span
                              className={`ps-status ${
                                item.pagamento_online_liberado
                                  ? "ps-status-on"
                                  : "ps-status-off"
                              }`}
                            >
                              {item.pagamento_online_liberado ? (
                                <FaCheck />
                              ) : (
                                <FaLock />
                              )}

                              {item.pagamento_online_liberado
                                ? "Liberado"
                                : "Bloqueado"}
                            </span>

                            <FaChevronRight className="ps-chevron" />
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : !itemAtual ? (
                <div className="ps-empty">
                  <FaExclamationCircle />
                  <strong>Barbearia não localizada</strong>
                  <span>
                    Atualize a lista antes de continuar.
                  </span>

                  <button
                    type="button"
                    className="ps-button ps-button-secondary"
                    onClick={voltarParaLista}
                  >
                    <FaArrowLeft /> Voltar à lista
                  </button>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    className="ps-back"
                    onClick={voltarParaLista}
                    disabled={savingId !== null}
                  >
                    <FaArrowLeft />
                    Todas as barbearias
                  </button>

                  <div className="ps-selected-store">
                    <div className="ps-store-icon ps-store-icon-selected">
                      <FaStore />
                    </div>

                    <div className="ps-selected-copy">
                      <span className="ps-eyebrow">UNIDADE SELECIONADA</span>
                      <h3>{itemAtual.nome || "Barbearia sem nome"}</h3>
                      <p>
                        {itemAtual.subdominio ||
                          `ID ${itemAtual.barbearia_id}`}
                      </p>
                    </div>

                    <span
                      className={`ps-status ${
                        itemAtual.pagamento_online_liberado
                          ? "ps-status-on"
                          : "ps-status-off"
                      }`}
                    >
                      {itemAtual.pagamento_online_liberado
                        ? "Liberado"
                        : "Bloqueado"}
                    </span>
                  </div>

                  <section className="ps-settings-section">
                    <div className="ps-section-heading">
                      <span className="ps-section-number">01</span>
                      <div>
                        <h3>Disponibilidade</h3>
                        <p>Controle de acesso ao recurso de pagamento.</p>
                      </div>
                    </div>

                    <label className="ps-toggle">
                      <span className="ps-toggle-copy">
                        <strong>Liberar pagamento online</strong>
                        <small>
                          Autoriza esta barbearia a utilizar o recurso,
                          respeitando as demais regras do sistema.
                        </small>
                      </span>

                      <input
                        type="checkbox"
                        checked={Boolean(
                          itemAtual.pagamento_online_liberado
                        )}
                        onChange={(event) =>
                          update(
                            itemAtual.barbearia_id,
                            "pagamento_online_liberado",
                            event.target.checked
                          )
                        }
                      />

                      <span className="ps-switch" aria-hidden="true" />
                    </label>
                  </section>

                  <section className="ps-settings-section">
                    <div className="ps-section-heading">
                      <span className="ps-section-number">02</span>
                      <div>
                        <h3>Percentuais permitidos</h3>
                        <p>
                          Defina quais opções poderão ser utilizadas.
                        </p>
                      </div>
                    </div>

                    <div className="ps-percent-grid">
                      {PERCENTUAIS.map((value) => {
                        const field = `permitir_${value}`;

                        return (
                          <label
                            className="ps-percent-option"
                            key={value}
                          >
                            <input
                              type="checkbox"
                              checked={Boolean(itemAtual[field])}
                              onChange={(event) =>
                                update(
                                  itemAtual.barbearia_id,
                                  field,
                                  event.target.checked
                                )
                              }
                            />

                            <span className="ps-percent-value">
                              {value}%
                            </span>
                            <span className="ps-percent-caption">
                              {value === 100
                                ? "Integral"
                                : value === 50
                                ? "Metade"
                                : "Entrada"}
                            </span>
                          </label>
                        );
                      })}
                    </div>

                    <div className="ps-inline-note">
                      <FaShieldAlt />
                      <span>
                        As permissões também precisam ser validadas pelo
                        backend antes de aceitar um pagamento.
                      </span>
                    </div>
                  </section>

                  <section className="ps-settings-section">
                    <div className="ps-section-heading">
                      <span className="ps-section-number">03</span>
                      <div>
                        <h3>Taxa da plataforma</h3>
                        <p>Valor da taxa Cronos para esta barbearia.</p>
                      </div>
                    </div>

                    <div className="ps-fee-box">
                      <label htmlFor="ps-taxa-cronos">
                        <FaPercentage />
                        Taxa Cronos
                      </label>

                      <div className="ps-fee-input-wrap">
                        <span className="ps-fee-prefix">R$</span>

                        <input
                          id="ps-taxa-cronos"
                          className="ps-input"
                          type="number"
                          min="0"
                          step="0.01"
                          inputMode="decimal"
                          value={itemAtual.taxa_cronos}
                          onChange={(event) =>
                            update(
                              itemAtual.barbearia_id,
                              "taxa_cronos",
                              event.target.value
                            )
                          }
                        />
                      </div>

                      <p className="ps-help">
                        Informe um valor igual ou superior a zero.
                      </p>
                    </div>
                  </section>

                  <section className="ps-current-info">
                    <div className="ps-info-chip">
                      <span>Pagamento atualmente ativo</span>
                      <strong>
                        {itemAtual.pagamento_online_ativo ? "Sim" : "Não"}
                      </strong>
                    </div>

                    <div className="ps-info-chip">
                      <span>Percentual configurado</span>
                      <strong>
                        {itemAtual.percentual_pagamento || 0}%
                      </strong>
                    </div>
                  </section>
                </>
              )}
            </div>

            {barbeariaSelecionada !== null &&
              itemAtual &&
              !busy && (
                <footer className="ps-modal-footer">
                  <div className="ps-footer-note">
                    <FaShieldAlt />
                    <span>
                      Configurações novas não devem alterar pagamentos
                      já iniciados.
                    </span>
                  </div>

                  <div className="ps-footer-actions">
                    <button
                      type="button"
                      className="ps-button ps-button-secondary"
                      onClick={voltarParaLista}
                      disabled={savingId !== null}
                    >
                      Voltar
                    </button>

                    <button
                      type="button"
                      className="ps-button ps-button-primary"
                      onClick={() => save(itemAtual)}
                      disabled={savingId !== null}
                    >
                      {savingId === itemAtual.barbearia_id ? (
                        <>
                          <FaSyncAlt className="ps-spinner" />
                          Salvando...
                        </>
                      ) : (
                        <>
                          <FaSave />
                          Salvar alterações
                        </>
                      )}
                    </button>
                  </div>
                </footer>
              )}
          </section>
        </div>
      )}
    </div>
  );
}
